/**
 * BancoAvatar (Bloco BSEL.3).
 *
 * Renderiza um avatar circular pequeno que representa um banco. Estrategia:
 *   - Bancos conhecidos (top 30) usam cor oficial da marca + iniciais
 *     curadas (BB, IT, BR, NU, etc).
 *   - Bancos desconhecidos caem no fallback: cor HSL deterministica
 *     baseada no hash do codigo + 2 letras do nomeCurto.
 *
 * Por que nao SVGs oficiais: licenciamento. Cores e iniciais sao fato
 * publico — banco do Brasil usa amarelo, Itau usa laranja+azul, etc.
 * Avatar colorido + letras eh visual reconhecivel + zero risco juridico.
 *
 * Quando virar problema visual, baixar SVGs oficiais (Brand guidelines
 * de cada banco geralmente sao publicos) e plugar via prop `srcSvg`.
 */

import React from "react";
import styled from "styled-components";

// Marcas conhecidas: cor oficial + iniciais ja curadas.
// Source: brand guidelines publicos de cada instituicao.
const MARCAS_CONHECIDAS = {
  "001": { cor: "#FAE128", corTexto: "#003D82", iniciais: "BB" },        // Banco do Brasil — amarelo/azul
  "003": { cor: "#FFB000", corTexto: "#003366", iniciais: "BA" },        // Amazonia — amarelo
  "004": { cor: "#9B1B1F", corTexto: "#FFFFFF", iniciais: "BN" },        // Nordeste — vermelho
  "021": { cor: "#005FA8", corTexto: "#FFFFFF", iniciais: "BE" },        // Banestes — azul
  "033": { cor: "#EC0000", corTexto: "#FFFFFF", iniciais: "SA" },        // Santander — vermelho
  "036": { cor: "#1F2D62", corTexto: "#FFFFFF", iniciais: "BB" },        // BBI — azul
  "037": { cor: "#005C2D", corTexto: "#FFCC00", iniciais: "BP" },        // Banpara — verde
  "041": { cor: "#0066B3", corTexto: "#FFFFFF", iniciais: "BR" },        // Banrisul — azul
  "047": { cor: "#003C71", corTexto: "#FFFFFF", iniciais: "BS" },        // Banese — azul
  "069": { cor: "#0066B3", corTexto: "#FFFFFF", iniciais: "CF" },        // Crefisa — azul
  "070": { cor: "#0072BC", corTexto: "#FFFFFF", iniciais: "BR" },        // BRB — azul
  "074": { cor: "#003E7E", corTexto: "#FFFFFF", iniciais: "JS" },        // J. Safra — azul
  "077": { cor: "#FF7A00", corTexto: "#FFFFFF", iniciais: "IN" },        // Inter — laranja
  "102": { cor: "#000000", corTexto: "#FFFF00", iniciais: "XP" },        // XP — preto
  "104": { cor: "#0070AF", corTexto: "#F39200", iniciais: "CE" },        // Caixa — azul/laranja
  "184": { cor: "#FF7300", corTexto: "#003366", iniciais: "IB" },        // Itau BBA — laranja
  "197": { cor: "#00A868", corTexto: "#FFFFFF", iniciais: "ST" },        // Stone — verde
  "208": { cor: "#0F1C2E", corTexto: "#C4A062", iniciais: "BT" },        // BTG — azul marinho
  "212": { cor: "#00AEEF", corTexto: "#FFFFFF", iniciais: "OR" },        // Original — azul claro
  "218": { cor: "#A50034", corTexto: "#FFFFFF", iniciais: "BS" },        // BS2 — vinho
  "237": { cor: "#CC092F", corTexto: "#FFFFFF", iniciais: "BR" },        // Bradesco — vermelho
  "243": { cor: "#1A1A1A", corTexto: "#D4AF37", iniciais: "MA" },        // Master — preto/dourado
  "246": { cor: "#003C71", corTexto: "#FFFFFF", iniciais: "AB" },        // ABC Brasil
  "260": { cor: "#820AD1", corTexto: "#FFFFFF", iniciais: "NU" },        // Nubank — roxo
  "290": { cor: "#1BA39C", corTexto: "#FFFFFF", iniciais: "PS" },        // PagSeguro
  "318": { cor: "#FFA500", corTexto: "#003366", iniciais: "BM" },        // BMG — laranja
  "323": { cor: "#00B5D8", corTexto: "#FFFFFF", iniciais: "MP" },        // Mercado Pago
  "335": { cor: "#1F2D62", corTexto: "#FFC000", iniciais: "DI" },        // Digio
  "336": { cor: "#1A1A1A", corTexto: "#FFFFFF", iniciais: "C6" },        // C6 — preto
  "341": { cor: "#EC7000", corTexto: "#003277", iniciais: "IT" },        // Itau — laranja/azul
  "348": { cor: "#000000", corTexto: "#FFFF00", iniciais: "XP" },        // Banco XP
  "380": { cor: "#21C25E", corTexto: "#FFFFFF", iniciais: "PP" },        // PicPay
  "389": { cor: "#003D7C", corTexto: "#FAE128", iniciais: "ME" },        // Mercantil
  "422": { cor: "#00357B", corTexto: "#FFFFFF", iniciais: "SF" },        // Safra
  "477": { cor: "#003B70", corTexto: "#FFFFFF", iniciais: "CI" },        // Citibank
  "623": { cor: "#005CA9", corTexto: "#FAE128", iniciais: "PA" },        // PAN
  "655": { cor: "#FFCB05", corTexto: "#003277", iniciais: "VO" },        // Votorantim
  "707": { cor: "#003D82", corTexto: "#FFFFFF", iniciais: "DA" },        // Daycoval
  "735": { cor: "#22A6B3", corTexto: "#FFFFFF", iniciais: "NE" },        // Neon
  "745": { cor: "#003B70", corTexto: "#FFFFFF", iniciais: "CI" },        // Citibank
  "748": { cor: "#3AAA35", corTexto: "#FFFFFF", iniciais: "SI" },        // Sicredi — verde
  "756": { cor: "#003641", corTexto: "#7AB800", iniciais: "SI" },        // Sicoob — verde escuro
};

/**
 * Hash deterministico simples para gerar cor HSL.
 */
function hashString(texto) {
  let h = 0;
  for (let i = 0; i < texto.length; i++) {
    h = (h * 31 + texto.charCodeAt(i)) >>> 0;
  }
  return h;
}

/**
 * Para bancos nao mapeados: gera cor HSL deterministica.
 * Hue 0-360 baseado no codigo; saturacao + luminosidade fixas pra
 * cores legiveis com texto branco.
 */
function corFallback(codigo) {
  const hue = hashString(codigo || "000") % 360;
  return `hsl(${hue}, 55%, 38%)`;
}

/**
 * 2 letras representativas do nomeCurto (defensivo se vazio).
 */
function iniciais(nomeCurto) {
  if (!nomeCurto) return "??";
  const limpo = nomeCurto.replace(/[^a-zA-ZÀ-ÿ ]/g, "").trim();
  const palavras = limpo.split(/\s+/).filter(Boolean);
  if (palavras.length === 0) return "??";
  if (palavras.length === 1) return palavras[0].slice(0, 2).toUpperCase();
  return (palavras[0][0] + palavras[1][0]).toUpperCase();
}

const Circle = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  border-radius: 50%;
  background: ${({ $cor }) => $cor};
  color: ${({ $corTexto }) => $corTexto};
  font-weight: 700;
  font-size: ${({ $size }) => Math.max(8, Math.round($size * 0.4))}px;
  line-height: 1;
  letter-spacing: 0;
  user-select: none;
  overflow: hidden;
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.04);
`;

/**
 * @param {Object} props
 * @param {string} props.codigo Codigo COMPE do banco (3 dig).
 * @param {string} [props.nomeCurto] Nome curto (fallback iniciais).
 * @param {number} [props.size=20] Tamanho em pixels.
 */
export default function BancoAvatar({ codigo, nomeCurto = "", size = 20 }) {
  const conhecido = codigo ? MARCAS_CONHECIDAS[codigo] : null;
  const cor = conhecido?.cor || corFallback(codigo);
  const corTexto = conhecido?.corTexto || "#FFFFFF";
  const letras = conhecido?.iniciais || iniciais(nomeCurto);

  return (
    <Circle
      $size={size}
      $cor={cor}
      $corTexto={corTexto}
      aria-hidden="true"
      title={nomeCurto || codigo}
    >
      {letras}
    </Circle>
  );
}
